import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInternalApiRequest } from 'shared/hooks/useInternalApiRequest';
import { InternalApiRequestError } from 'shared/hooks/useInternalApiRequest/types';
import { formatSubscriptionData, getShopifyContractId } from 'shared/utils/subscriptionV2Helpers';
import { useStoreValue } from 'state/hooks/useStore';
import { SUBSCRIPTION_API_URL } from '@portal/config/base';
import { queryKeyForSubscriptionDetailsV2 } from '../getSubscriptionDetails';
import { SubscriptionDetailsV2Response } from '../getSubscriptionDetails/types';
import { convertCollectionProductToLineItem, getFallbackProduct } from './utils';
import { getQueryKeyForGetSubscriptionsV2 } from '../getSubscriptions';
import { SubscriptionsResponseV2 } from '../getSubscriptions/types';

export function useMutationAddSubscriptionLineItemV2() {
  const queryClient = useQueryClient();
  const request = useInternalApiRequest();
  const store = useStoreValue();

  return useMutation<any, InternalApiRequestError, any>({
    mutationFn: async ({ contractId, body, isOneTime }) => {
      if (contractId && contractId !== -1) {
        const oneTimeQueryParam = isOneTime ? '?type=otp' : '';
        return request({
          method: 'POST',
          url: `${SUBSCRIPTION_API_URL[store]}/user/subscription/${contractId}/line-item${oneTimeQueryParam}`,
          data: body
        });
      }
      return Promise.resolve({});
    },
    onSuccess: (
      result,
      { contractId, body: { shopify_product_variant_id, quantity }, lineItem }
    ) => {
      const formattedLineItem = lineItem
        ? convertCollectionProductToLineItem(lineItem, quantity)
        : getFallbackProduct(result, shopify_product_variant_id);

      // update the cached subscription details data optimistically
      queryClient.setQueryData(
        queryKeyForSubscriptionDetailsV2(store, contractId),
        (subscriptionDetails: SubscriptionDetailsV2Response) => {
          if (!subscriptionDetails) return subscriptionDetails;
          return {
            ...subscriptionDetails,
            ...formatSubscriptionData(result)
          };
        }
      );

      // update the cached subscription list data
      queryClient.setQueryData(
        getQueryKeyForGetSubscriptionsV2(store),
        (subscriptionList: SubscriptionsResponseV2) => {
          if (subscriptionList) {
            const updatedList = subscriptionList.map((subscription) =>
              getShopifyContractId(subscription.shopify_contract_id) === contractId
                ? formatSubscriptionData({
                    ...subscription,
                    line_items: [...subscription.line_items, formattedLineItem]
                  })
                : subscription
            );

            return updatedList;
          }

          return [];
        }
      );

      return contractId;
    }
  });
}
